/**
 * ============================================================================
 * Production 模块 API 工厂函数（多子模块聚合模式）
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 Production（生产管理）模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式 + 多子模块聚合
 * - 工厂函数：createProductionApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * - 多子模块：包含7个子资源的API方法（ProductionOrder, WorkOrder, WorkReport等）
 * 
 * 📦 包含的子资源：
 * 1. ProductionOrder（生产订单）- 生产订单的创建、审批、执行、完成等全生命周期管理
 * 2. WorkOrder（工单）- 工单的创建、派发、接单、执行、完成等全生命周期管理
 * 3. WorkReport（报工）- 报工提交、撤销、统计等
 * 4. MaterialConsumption（物料投料）- 物料投料、退料、BOM 防错校验等
 * 5. ScrapOrder（报废单）- 报废单的创建、审核、查询等
 * 6. ReworkOrder（返工单）- 返工单的创建、审核、执行、完成等
 * 7. ProductionBatch（生产批次）- 批次查询、追溯链查看、标签重打、容器更换等
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * - 多子模块聚合：保持业务模块的完整性，符合后端结构
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 创建适配器（让 Vben 的 requestClient 符合 IRequestClient 接口）
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   post: (url, data, config) => requestClient.post(url, data, config).then(res => res.data),
 *   put: (url, data, config) => requestClient.put(url, data, config).then(res => res.data),
 *   delete: (url, config) => requestClient.delete(url, config).then(res => res.data),
 * }
 * 
 * // 2. 调用工厂函数，传入适配器
 * import { createProductionApi } from '@bayes-mes/shared/api/production/factory'
 * export const productionApi = createProductionApi(webAdapter)
 * 
 * // 3. 使用生成的 API（按子资源分组）
 * // 生产订单相关
 * const orders = await productionApi.getProductionOrderList({ page: 1 })
 * const order = await productionApi.getProductionOrderDetail(123)
 * await productionApi.acceptProductionOrder(123, { note: '已接单' })
 * 
 * // 工单相关
 * const workOrders = await productionApi.getWorkOrderList({ page: 1 })
 * const workOrder = await productionApi.getWorkOrderDetail(456)
 * await productionApi.acceptWorkOrder(456, { note: '已接单' })
 * 
 * // 报工相关
 * await productionApi.createWorkReport({ work_order_id: 456, quantity: 10 })
 * 
 * // ... 其他子资源的API方法
 * ```
 * 
 * **App 端（uni-app X）**：
 * ```typescript
 * // 1. 创建适配器（让 uni.request 符合 IRequestClient 接口）
 * const uniAdapter: IRequestClient = {
 *   get: (url, config) => new Promise((resolve, reject) => {
 *     uni.request({
 *       url,
 *       method: 'GET',
 *       data: config?.params,
 *       success: (res) => resolve(res.data.data),
 *       fail: reject
 *     })
 *   }),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数
 * import { createProductionApi } from '@bayes-mes/shared/api/production/factory'
 * export const productionApi = createProductionApi(uniAdapter)
 * ```
 * 
 * ⚠️ 注意事项：
 * - **请求客户端接口**：所有工厂函数都使用 `api/core/client.ts` 中定义的 `IRequestClient` 接口
 * - **不要重复定义接口**：从 `../core/client` 导入，不要在每个模块中重新定义
 * - **适配器职责**：适配器负责将平台特定的请求客户端转换为符合 `IRequestClient` 的对象
 * - **响应数据解包**：适配器需要处理响应数据的解包（如 `res.data.data`）
 * - **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * - **注释详细**：每个方法都有详细的中文注释，包括使用场景、参数、返回值、数据流向
 * - **多子模块组织**：API方法按子资源分组，使用注释分隔，便于查找和维护
 * 
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 * - 新增子资源时
 * 
 * ✅ 验证方式：
 * - 确保所有 Controller 的 public 方法都已映射
 * - 确保所有类型导入正确
 * - 确保 URL 路径与后端路由一致（从 API_ROUTES 引用）
 * - 运行 `npm run type-check` 验证 TypeScript 编译
 * 
 * @module api/production/factory
 * @description Production（生产管理）模块的 API 工厂函数（多子模块聚合模式）
 * 
 * @source 对应后端文件:
 * - backend/routes/api.php (主路由文件)
 * - backend/app/Modules/Production/Routes/api.php (Production模块路由)
 * - backend/app/Modules/Production/Controllers/ProductionOrderController.php
 * - backend/app/Modules/Production/Controllers/WorkOrderController.php
 * - backend/app/Modules/Production/Controllers/WorkReportController.php
 * - backend/app/Modules/Production/Controllers/MaterialConsumptionController.php
 * - backend/app/Modules/Production/Controllers/ScrapOrderController.php
 * - backend/app/Modules/Production/Controllers/ReworkOrderController.php
 * - backend/app/Modules/Production/Controllers/ProductionBatchController.php
 * 
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-11-26
 * @last-updated 2025-11-26
 * 
 * @change-log
 * - 2025-11-26: 初始创建，实现所有7个子资源的API方法
 * 
 * @notes
 * - API 路径前缀: /api/v1/production
 * - 使用工厂模式实现平台无关的 API 调用
 * - 支持完整的 TypeScript 类型推断
 * - IRequestClient 接口定义在 api/core/client.ts
 * - 使用模式四（多子模块聚合），保持业务模块完整性
 */

// ============================================================================
// 类型导入
// ============================================================================

import type {
  // 响应类型
  ProductionOrder,
  WorkOrder,
  WorkOrderOperation,
  WorkReport,
  MaterialConsumption,
  ScrapOrder,
  ReworkOrder,
  ProductionBatch,
  BatchTraceResult,
  OperationMaterial,
  MaterialValidationResponse,
  // 产量统计四维度（2026-09-10 新增）
  OutputStatisticQueryParams,
  OutputStatisticListResponse,
  // 多级BOM工单响应类型（2025-12-15 新增）
  WorkOrderTreeNode,
  ChildWorkOrderStatusResponse,
  GenerateMultiLevelWorkOrderResponse,
  OutputInboundResponse,
  AvailableSemiBatch,
  ConsumeSemiResponse,
  WipBatch,
  WipEquipment,
  WipOverview,
  WipRealtimeInventory,
  ProductOperationProgressResponse,
  // 请求类型
  CreateProductionOrderForm,
  UpdateProductionOrderForm,
  AcceptProductionOrderForm,
  CancelProductionOrderForm,
  CreateWorkOrderForm,
  UpdateWorkOrderForm,
  UpdateWorkOrderRemarkForm,
  AcceptWorkOrderForm,
  StartWorkOrderForm,
  PauseWorkOrderForm,
  CompleteWorkOrderForm,
  CancelWorkOrderForm,
  CreateWorkReportForm,
  CancelWorkReportForm,
  ForceCancelWorkReportForm,
  CreateMaterialConsumptionForm,
  MaterialCheckBomRequest,
  CreateScrapOrderForm,
  UpdateScrapOrderForm,
  ApproveScrapOrderForm,
  RejectScrapOrderForm,
  CreateReworkOrderForm,
  UpdateReworkOrderForm,
  ApproveReworkOrderForm,
  RejectReworkOrderForm,
  CompleteReworkOrderForm,
  // 多级BOM工单请求类型（2025-12-15 新增）
  GenerateMultiLevelWorkOrderForm,
  OutputInboundForm,
  // 查询参数类型
  ProductionOrderQueryParams,
  WorkOrderQueryParams,
  WipBatchQueryParams,
  WipEquipmentQueryParams,
  WipOverviewQueryParams,
  WipRealtimeInventoryQueryParams,
  ProductOperationProgressQueryParams,
  WorkReportQueryParams,
  MaterialConsumptionQueryParams,
  ScrapOrderQueryParams,
  ReworkOrderQueryParams,
  ProductionBatchQueryParams,
  // 统计类型
  ProductionOrderStatusStatistics,
  WorkOrderStatusStatistics,
  OperatorStatisticsItem,
  WorkstationStatisticsItem,
  DailyStatisticsItem,
  // 报废单/返工单统计类型
  ScrapStatsSummary,
  ScrapStatsByType,
  ScrapStatsByDisposal,
  ReworkStatsSummary,
  ReworkStatsByType,
  ExceptionStatsByMaterial,
  ExceptionStatsByReason,
  ExceptionStatsTrend,
  // 统计查询参数
  ScrapStatisticsQueryParams,
  ReworkStatisticsQueryParams,
  ExceptionStatsTrendQueryParams,
  // 工单分配类型（Phase 2 MPS）
  DispatchPreviewRequest,
  DispatchConfirmRequest,
  LineCapacityQueryParams,
  DispatchPreviewResult,
  DispatchConfirmResult,
  LineCapacitySummary,
  // 变更记录类型（Phase 4）
  ProductionOrderChangelog,
  // 计划工序进度类型（2025-12-22 新增）
  PlanOperationProgressResponse,
  // 工单转移类型（2026-02-11 新增）
  TransferableTarget,
  TransferPreviewResult,
  TransferPreviewRequest,
  TransferRequest,
  TransferHistoryRecord,
  // 工单变更历史类型（含备注修改留痕）
  WorkOrderChangeHistory,
  // 工序变更类型（v4.0 新增）
  OperationChangeRecord,
} from '../../types/production'

import type { 
  PaginationResult,
} from '../../types/api'

// ============================================================================
// 导入统一的请求客户端接口（⚠️ 重要）
// ============================================================================
// 不要重复定义接口！所有工厂函数共享同一个接口
import type { IRequestClient } from '../core/client'

// ============================================================================
// 导入 API 路由定义
// ============================================================================
import { API_ROUTES } from '../routes'

// ============================================================================
// Production API 工厂函数
// ============================================================================

/**
 * 创建 Production 模块的 API 方法集合
 * 
 * @description
 * 这是一个工厂函数，接收一个请求客户端，返回一组 API 方法。
 * 
 * **工厂模式的核心思想**：
 * - 输入：一个能发送 HTTP 请求的客户端（符合 IRequestClient 接口）
 * - 输出：一组封装好的 API 方法（具有完整类型安全）
 * - 好处：同一套 API 定义，可以在不同平台使用（Web、App）
 * 
 * **多子模块聚合模式**：
 * - Production模块包含7个子资源，每个子资源都有独立的API方法
 * - 所有方法统一在一个工厂函数中，保持业务模块的完整性
 * - 方法按子资源分组，使用注释分隔，便于查找和维护
 * 
 * **为什么不直接写死 axios 或 uni.request？**
 * - 因为 Shared 库是跨平台的，不能依赖特定平台的请求库
 * - 通过依赖注入，让使用者提供自己的请求客户端
 * - 这样 Web 端可以用 axios，App 端可以用 uni.request
 * 
 * @param client 请求客户端（必须符合 IRequestClient 接口）
 * @returns Production 模块的所有 API 方法（包含7个子资源）
 * 
 * @example
 * // Web 端使用
 * import { createProductionApi } from '@bayes-mes/shared/api/production/factory'
 * import { webAdapter } from '@/api/adapters/http'
 * 
 * export const productionApi = createProductionApi(webAdapter)
 * 
 * // 调用 API（生产订单）
 * const orders = await productionApi.getProductionOrderList({ page: 1 })
 * const order = await productionApi.getProductionOrderDetail(123)
 * 
 * // 调用 API（工单）
 * const workOrders = await productionApi.getWorkOrderList({ page: 1 })
 * await productionApi.acceptWorkOrder(456, { note: '已接单' })
 * 
 * @example
 * // App 端使用
 * import { createProductionApi } from '@bayes-mes/shared/api/production/factory'
 * import { uniAdapter } from '@/api/adapters/http'
 * 
 * export const productionApi = createProductionApi(uniAdapter)
 */
export function createProductionApi(client: IRequestClient) {
  return {
    // ========================================================================
    // 1. 生产订单（ProductionOrder）相关 API
    // ========================================================================
    // 📌 API 前缀：/api/v1/production/orders
    // 📌 功能：生产订单的创建、审批、执行、完成等全生命周期管理

    /**
     * 获取生产订单列表（分页）
     * 
     * 使用场景：
     * - 在生产订单管理页面的主列表中使用
     * - 支持分页、搜索、筛选功能
     * - 用户可以根据各种条件查询生产订单
     * 
     * @param params - 查询参数对象（可选）
     *   - page?: number - 页码，从 1 开始，默认 1
     *   - per_page?: number - 每页数量，默认 20，最大 100
     *   - order_no?: string - 订单编号（精确匹配）
     *   - sales_order_no?: string - 销售订单编号（精确匹配）
     *   - status?: ProductionOrderStatus | ProductionOrderStatus[] - 订单状态（单个或数组）
     *   - priority?: ProductionOrderPriority - 优先级
     *   - material_code?: string - 物料编码（模糊匹配）
     *   - material_name?: string - 物料名称（模糊匹配）
     *   - plan_start_from?: string - 计划开始日期（起始，ISO 8601格式）
     *   - plan_start_to?: string - 计划开始日期（结束，ISO 8601格式）
     *   - plan_end_from?: string - 计划结束日期（起始，ISO 8601格式）
     *   - plan_end_to?: string - 计划结束日期（结束，ISO 8601格式）
     *   - keyword?: string - 搜索关键词（搜索订单编号、物料编码、物料名称）
     *   - sort_by?: string - 排序字段（如 'created_at', 'order_no'）
     *   - sort_order?: 'asc' | 'desc' - 排序方向，默认 'desc'
     * 
     * @returns Promise<PaginationResult<ProductionOrder>> 分页结果对象
     *   返回的数据结构：
     *   {
     *     data: ProductionOrder[],        // 生产订单列表数组
     *     total: number,                 // 总记录数
     *     current_page: number,          // 当前页码
     *     per_page: number,               // 每页数量
     *     last_page: number,             // 最后一页页码
     *     from: number,                  // 当前页起始记录号
     *     to: number                     // 当前页结束记录号
     *   }
     * 
     * 数据流向（完整过程）：
     * 1. 用户操作：用户在页面上点击"下一页"按钮或输入搜索关键词
     * 2. 前端调用：组件调用 productionApi.getProductionOrderList({ page: 2, keyword: 'PO001' })
     * 3. 适配器处理：适配器将参数转换为 HTTP 请求
     *    - Web端：axios 发送 GET 请求
     *    - App端：uni.request 发送 GET 请求
     * 4. 网络传输：发送到后端 GET /api/v1/production/orders?page=2&keyword=PO001
     * 5. 后端处理：
     *    - 路由匹配到 ProductionOrderController@index 方法
     *    - 验证查询参数（ProductionOrderQueryParams）
     *    - 查询数据库，应用筛选条件和分页
     *    - 通过 ProductionOrderResource 格式化数据（snake_case → camelCase）
     * 6. 响应返回：后端返回 JSON 格式的分页数据
     * 7. 适配器解包：适配器提取 response.data.data（实际数据）
     * 8. 前端接收：组件收到 PaginationResult<ProductionOrder> 类型的数据
     * 9. 页面更新：使用数据更新表格显示
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：获取第一页（使用默认参数）
     * const page1 = await productionApi.getProductionOrderList()
     * console.log(page1.data)  // [ProductionOrder对象数组]
     * console.log(page1.total) // 总记录数
     * 
     * // 示例2：获取第二页，每页20条
     * const page2 = await productionApi.getProductionOrderList({
     *   page: 2,
     *   per_page: 20
     * })
     * 
     * // 示例3：搜索功能
     * const searchResult = await productionApi.getProductionOrderList({
     *   keyword: 'PO001'
     * })
     * 
     * // 示例4：按状态筛选
     * const pendingOrders = await productionApi.getProductionOrderList({
     *   status: ProductionOrderStatus.PENDING
     * })
     * 
     * // 示例5：组合查询（分页 + 搜索 + 排序）
     * const result = await productionApi.getProductionOrderList({
     *   page: 1,
     *   per_page: 10,
     *   keyword: 'PO001',
     *   status: [ProductionOrderStatus.PENDING, ProductionOrderStatus.ACCEPTED],
     *   sort_by: 'created_at',
     *   sort_order: 'desc'
     * })
     * ```
     * 
     * 错误处理：
     * - 404：资源不存在（路由错误或后端未实现）
     * - 422：参数验证失败（page 不是数字等）
     * - 500：服务器内部错误（数据库查询失败等）
     */
    getProductionOrderList(params?: ProductionOrderQueryParams) {
      return client.get<PaginationResult<ProductionOrder>>(
        API_ROUTES.production.list,
        { params }
      )
    },

    /**
     * 获取生产订单详情
     * 
     * 使用场景：
     * - 在生产订单详情页面显示完整信息
     * - 编辑生产订单时获取当前数据
     * - 查看生产订单的详细资料
     * 
     * @param id - 生产订单的唯一标识符（主键）
     *   示例：id: 123（获取 ID 为 123 的生产订单）
     * 
     * @returns Promise<ProductionOrder> 单个生产订单对象
     *   返回的数据结构：
     *   {
     *     id: number,                    // 生产订单 ID
     *     orderNo: string,               // 订单编号
     *     salesOrderId: number | null,  // 关联的销售订单ID
     *     salesOrderNo: string | null,   // 关联的销售订单编号
     *     materialId: number,            // 物料ID
     *     materialCode: string,          // 物料编码
     *     materialName: string,          // 物料名称
     *     orderQuantity: number,         // 订单数量
     *     producedQuantity: number,      // 已生产数量
     *     qualifiedQuantity: number,      // 合格数量
     *     rejectedQuantity: number,       // 不合格数量
     *     status: ProductionOrderStatus, // 订单状态
     *     priority: ProductionOrderPriority, // 优先级
     *     planStartDate: string | null,  // 计划开始日期
     *     planEndDate: string | null,    // 计划结束日期
     *     actualStartDate: string | null, // 实际开始日期
     *     actualEndDate: string | null,  // 实际结束日期
     *     // ... 其他字段
     *     createdAt: string,             // 创建时间（ISO 8601格式）
     *     updatedAt: string               // 更新时间
     *   }
     * 
     * 数据流向：
     * 1. 用户点击列表中的某一行，或在 URL 中输入详情页地址
     * 2. 前端调用：productionApi.getProductionOrderDetail(123)
     * 3. 发送请求：GET /api/v1/production/orders/123
     * 4. 后端处理：ProductionOrderController@show 方法
     *    - 验证 ID 参数
     *    - 从数据库查询指定 ID 的记录
     *    - 如果不存在，返回 404
     *    - 通过 ProductionOrderResource 格式化数据
     * 5. 返回数据：单个 ProductionOrder 对象
     * 6. 前端显示：在详情页或编辑表单中展示数据
     * 
     * 代码示例：
     * ```typescript
     * // 获取 ID 为 123 的生产订单
     * const detail = await productionApi.getProductionOrderDetail(123)
     * console.log(detail.orderNo)  // 订单编号
     * console.log(detail.materialName)  // 物料名称
     * 
     * // 在详情页组件中使用
     * const { id } = useRoute().params
     * const orderData = await productionApi.getProductionOrderDetail(Number(id))
     * ```
     * 
     * 错误处理：
     * - 404：生产订单不存在
     * - 403：无权限访问该生产订单
     * - 422：ID 格式错误（不是数字）
     */
    getProductionOrderDetail(id: number) {
      return client.get<ProductionOrder>(API_ROUTES.production.detail(id))
    },

    /**
     * 创建生产订单
     * 
     * 使用场景：
     * - 在生产订单管理页面点击"新建"按钮
     * - 提交创建生产订单的表单
     * - 从销售订单生成生产订单
     * 
     * @param data - 创建生产订单的表单数据
     *   字段要求（参考 CreateProductionOrderForm 类型）：
     *   - order_no?: string - 订单编号（可选，不提供时自动生成）
     *   - sales_order_id?: number - 关联的销售订单ID（可选）
     *   - material_id: number - 物料ID（必填）
     *   - material_code?: string - 物料编码（可选，用于显示）
     *   - material_name?: string - 物料名称（可选，用于显示）
     *   - route_id?: number - 工艺路线ID（可选）
     *   - bom_id?: number - BOM ID（可选）
     *   - order_quantity: number - 订单数量（必填，必须大于0）
     *   - plan_start_date: string - 计划开始日期（必填，ISO 8601格式）
     *   - plan_end_date: string - 计划结束日期（必填，ISO 8601格式）
     *   - status?: number - 订单状态（可选，默认待接单）
     *   - priority?: number - 优先级（可选，默认中）
     *   - source_type?: string - 来源类型（可选，'SALES_ORDER' 或 'MANUAL'）
     *   - remark?: string - 备注（可选）
     * 
     * @returns Promise<ProductionOrder> 创建成功后的生产订单对象（包含生成的 ID）
     *   返回的数据：
     *   {
     *     id: number,          // 自动生成的 ID
     *     orderNo: string,     // 订单编号（自动生成或使用提交的编号）
     *     materialId: number,  // 提交的物料ID
     *     orderQuantity: number, // 提交的订单数量
     *     // ... 其他字段
     *     createdAt: string,   // 服务器生成的创建时间
     *     updatedAt: string    // 服务器生成的更新时间
     *   }
     * 
     * 数据流向：
     * 1. 用户填写创建表单，点击"提交"按钮
     * 2. 前端验证：表单验证（必填项、格式等）
     * 3. 前端调用：productionApi.createProductionOrder({ material_id: 1, order_quantity: 100, ... })
     * 4. 发送请求：POST /api/v1/production/orders
     *    请求体：{ material_id: 1, order_quantity: 100, ... }（JSON格式，snake_case）
     * 5. 后端处理：ProductionOrderController@store 方法
     *    - 验证数据（使用 StoreProductionOrderRequest）
     *    - 检查唯一性（订单编号不能重复）
     *    - 插入数据库
     *    - 返回新创建的记录（包含自动生成的 ID 和时间戳）
     * 6. 前端处理：
     *    - 显示成功提示
     *    - 跳转到列表页或详情页
     *    - 刷新列表数据
     * 
     * 代码示例：
     * ```typescript
     * // 创建新生产订单
     * const newOrder: CreateProductionOrderForm = {
     *   material_id: 1,
     *   order_quantity: 100,
     *   plan_start_date: '2025-12-01T00:00:00Z',
     *   plan_end_date: '2025-12-31T23:59:59Z',
     *   priority: ProductionOrderPriority.HIGH,
     *   remark: '紧急订单'
     * }
     * 
     * const created = await productionApi.createProductionOrder(newOrder)
     * console.log(created.id)  // 新创建的 ID
     * console.log(created.orderNo)  // 自动生成的订单编号
     * 
     * // 在表单提交中使用
     * const handleSubmit = async (formData: CreateProductionOrderForm) => {
     *   try {
     *     const result = await productionApi.createProductionOrder(formData)
     *     message.success('创建成功')
     *     router.push(`/production/orders/${result.id}`)  // 跳转到详情页
     *   } catch (error) {
     *     message.error('创建失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 422：数据验证失败
     *   - 必填字段缺失
     *   - 字段格式错误
     *   - 订单编号重复（违反唯一性约束）
     * - 403：无权限创建
     * - 500：服务器错误（数据库插入失败）
     */
    createProductionOrder(data: CreateProductionOrderForm) {
      return client.post<ProductionOrder>(API_ROUTES.production.orders, data)
    },

    /**
     * 更新生产订单
     * 
     * 使用场景：
     * - 在生产订单编辑页面修改后提交
     * - 更新生产订单的部分字段
     * - 修改计划日期、数量等信息
     * 
     * @param id - 要更新的生产订单的 ID
     * @param data - 更新的数据（只需要包含要修改的字段）
     *   字段要求（参考 UpdateProductionOrderForm 类型）：
     *   - 可以只包含部分字段（部分更新）
     *   - 不能修改的字段：id, createdAt（后端会忽略）
     *   - 状态字段需要通过专门的状态操作方法修改（accept, approve, start等）
     * 
     * @returns Promise<ProductionOrder> 更新后的完整生产订单对象
     * 
     * 数据流向：
     * 1. 用户在编辑页面修改字段，点击"保存"
     * 2. 前端调用：productionApi.updateProductionOrder(123, { plan_end_date: '2025-12-31' })
     * 3. 发送请求：PUT /api/v1/production/orders/123
     *    请求体：{ plan_end_date: '2025-12-31' }（只包含修改的字段）
     * 4. 后端处理：ProductionOrderController@update 方法
     *    - 验证 ID 是否存在
     *    - 验证更新数据（使用 UpdateProductionOrderRequest）
     *    - 更新数据库记录
     *    - 返回更新后的完整数据
     * 5. 前端处理：
     *    - 显示成功提示
     *    - 更新页面显示的数据
     * 
     * 代码示例：
     * ```typescript
     * // 更新生产订单的计划结束日期
     * const updated = await productionApi.updateProductionOrder(123, {
     *   plan_end_date: '2025-12-31T23:59:59Z'
     * })
     * 
     * // 更新多个字段
     * const updated2 = await productionApi.updateProductionOrder(123, {
     *   order_quantity: 150,
     *   priority: ProductionOrderPriority.EMERGENCY,
     *   remark: '更新备注'
     * })
     * 
     * // 在编辑表单中使用
     * const handleUpdate = async (id: number, formData: UpdateProductionOrderForm) => {
     *   try {
     *     const result = await productionApi.updateProductionOrder(id, formData)
     *     message.success('更新成功')
     *     // 更新本地数据
     *     orderData.value = result
     *   } catch (error) {
     *     message.error('更新失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：生产订单不存在
     * - 422：数据验证失败
     * - 403：无权限修改
     * - 409：冲突（如订单编号重复）
     */
    updateProductionOrder(id: number, data: UpdateProductionOrderForm) {
      return client.put<ProductionOrder>(API_ROUTES.production.detail(id), data)
    },

    /**
     * 删除生产订单
     * 
     * 使用场景：
     * - 在生产订单列表中点击"删除"按钮
     * - 在生产订单详情页删除当前记录
     * - 批量删除多个生产订单（需要后端支持）
     * 
     * @param id - 要删除的生产订单的 ID
     * 
     * @returns Promise<void> 删除成功不返回数据
     * 
     * 数据流向：
     * 1. 用户点击删除按钮，弹出确认对话框
     * 2. 用户确认删除
     * 3. 前端调用：productionApi.deleteProductionOrder(123)
     * 4. 发送请求：DELETE /api/v1/production/orders/123
     * 5. 后端处理：ProductionOrderController@destroy 方法
     *    - 验证 ID 是否存在
     *    - 检查是否可以删除（关联数据检查，如已有关联工单则不能删除）
     *    - 软删除或硬删除记录
     *    - 返回成功响应
     * 6. 前端处理：
     *    - 显示成功提示
     *    - 从列表中移除该记录
     *    - 刷新列表数据
     * 
     * 代码示例：
     * ```typescript
     * // 删除指定生产订单
     * await productionApi.deleteProductionOrder(123)
     * console.log('删除成功')
     * 
     * // 在列表中使用（带确认）
     * const handleDelete = async (id: number) => {
     *   const confirmed = await showConfirm('确定要删除吗？')
     *   if (!confirmed) return
     *   
     *   try {
     *     await productionApi.deleteProductionOrder(id)
     *     message.success('删除成功')
     *     // 刷新列表
     *     await loadList()
     *   } catch (error) {
     *     message.error('删除失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：生产订单不存在（可能已被删除）
     * - 403：无权限删除
     * - 409：无法删除（存在关联数据）
     *   例如：该生产订单下有关联的工单，需要先删除工单
     */
    deleteProductionOrder(id: number) {
      return client.delete<void>(API_ROUTES.production.delete(id))
    },

    /**
     * 接单（接受生产订单）
     * 
     * 使用场景：
     * - 生产部门接受生产订单
     * - 将订单状态从"待接单"变为"已接单"
     * - 记录接单人和接单时间
     * 
     * @param id - 生产订单的 ID
     * @param data - 接单数据（可选）
     *   - note?: string - 接单备注（可选）
     * 
     * @returns Promise<ProductionOrder> 更新后的生产订单对象
     * 
     * 数据流向：
     * 1. 用户在生产订单详情页点击"接单"按钮
     * 2. 前端调用：productionApi.acceptProductionOrder(123, { note: '已接单，预计12月1日开始生产' })
     * 3. 发送请求：POST /api/v1/production/orders/123/accept
     *    请求体：{ note: '已接单，预计12月1日开始生产' }（可选）
     * 4. 后端处理：ProductionOrderController@accept 方法
     *    - 验证订单状态（必须是"待接单"状态）
     *    - 更新订单状态为"已接单"
     *    - 记录接单人和接单时间
     *    - 保存接单备注（如果有）
     *    - 返回更新后的订单
     * 5. 前端处理：
     *    - 显示成功提示
     *    - 更新页面显示的状态
     * 
     * 代码示例：
     * ```typescript
     * // 接单（不带备注）
     * const accepted = await productionApi.acceptProductionOrder(123)
     * console.log(accepted.status)  // ProductionOrderStatus.ACCEPTED
     * 
     * // 接单（带备注）
     * const accepted2 = await productionApi.acceptProductionOrder(123, {
     *   note: '已接单，预计12月1日开始生产'
     * })
     * 
     * // 在详情页中使用
     * const handleAccept = async () => {
     *   try {
     *     const result = await productionApi.acceptProductionOrder(orderId.value, {
     *       note: acceptNote.value
     *     })
     *     message.success('接单成功')
     *     orderData.value = result  // 更新本地数据
     *   } catch (error) {
     *     message.error('接单失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：生产订单不存在
     * - 400：订单状态不允许接单（如已接单、已完成等）
     * - 403：无权限接单
     */
    acceptProductionOrder(id: number, data?: AcceptProductionOrderForm) {
      return client.post<ProductionOrder>(API_ROUTES.production.accept(id), data)
    },

    /**
     * 审核通过生产订单
     * 
     * 使用场景：
     * - 审核人员审核生产订单
     * - 将订单状态从"已接单"变为"进行中"（如果审核通过后自动开始）
     * - 记录审核人和审核时间
     * 
     * @param id - 生产订单的 ID
     * 
     * @returns Promise<ProductionOrder> 更新后的生产订单对象
     * 
     * 数据流向：
     * 1. 用户在审核页面点击"审核通过"按钮
     * 2. 前端调用：productionApi.approveProductionOrder(123)
     * 3. 发送请求：POST /api/v1/production/orders/123/approve
     * 4. 后端处理：ProductionOrderController@approve 方法
     *    - 验证订单状态（必须是"已接单"状态）
     *    - 更新订单状态（根据业务规则，可能变为"进行中"）
     *    - 记录审核人和审核时间
     *    - 返回更新后的订单
     * 5. 前端处理：
     *    - 显示成功提示
     *    - 更新页面显示的状态
     * 
     * 代码示例：
     * ```typescript
     * // 审核通过
     * const approved = await productionApi.approveProductionOrder(123)
     * console.log(approved.status)  // 更新后的状态
     * 
     * // 在审核页面中使用
     * const handleApprove = async () => {
     *   try {
     *     const result = await productionApi.approveProductionOrder(orderId.value)
     *     message.success('审核通过')
     *     router.push('/production/orders')  // 返回列表页
     *   } catch (error) {
     *     message.error('审核失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：生产订单不存在
     * - 400：订单状态不允许审核（如未接单、已完成等）
     * - 403：无权限审核
     */
    approveProductionOrder(id: number) {
      return client.post<ProductionOrder>(API_ROUTES.production.approveApplication(id))
    },

    /**
     * 开始生产
     * 
     * 使用场景：
     * - 开始执行生产订单
     * - 将订单状态从"已接单"或"已审核"变为"进行中"
     * - 记录实际开始时间
     * 
     * @param id - 生产订单的 ID
     * 
     * @returns Promise<ProductionOrder> 更新后的生产订单对象
     * 
     * 数据流向：
     * 1. 用户在生产订单详情页点击"开始生产"按钮
     * 2. 前端调用：productionApi.startProductionOrder(123)
     * 3. 发送请求：POST /api/v1/production/orders/123/start
     * 4. 后端处理：ProductionOrderController@start 方法
     *    - 验证订单状态（必须是"已接单"或"已审核"状态）
     *    - 更新订单状态为"进行中"
     *    - 记录实际开始时间（actualStartDate）
     *    - 返回更新后的订单
     * 5. 前端处理：
     *    - 显示成功提示
     *    - 更新页面显示的状态和开始时间
     * 
     * 代码示例：
     * ```typescript
     * // 开始生产
     * const started = await productionApi.startProductionOrder(123)
     * console.log(started.status)  // ProductionOrderStatus.IN_PROGRESS
     * console.log(started.actualStartDate)  // 实际开始时间
     * 
     * // 在详情页中使用
     * const handleStart = async () => {
     *   try {
     *     const result = await productionApi.startProductionOrder(orderId.value)
     *     message.success('生产已开始')
     *     orderData.value = result
     *   } catch (error) {
     *     message.error('开始失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：生产订单不存在
     * - 400：订单状态不允许开始（如未接单、已完成等）
     * - 403：无权限操作
     */
    startProductionOrder(id: number) {
      return client.post<ProductionOrder>(API_ROUTES.production.start(id))
    },

    /**
     * 完成生产订单
     * 
     * 使用场景：
     * - 完成生产订单的生产
     * - 将订单状态从"进行中"变为"已完成"
     * - 记录实际结束时间
     * 
     * @param id - 生产订单的 ID
     * 
     * @returns Promise<ProductionOrder> 更新后的生产订单对象
     * 
     * 数据流向：
     * 1. 用户在生产订单详情页点击"完成"按钮
     * 2. 前端调用：productionApi.completeProductionOrder(123)
     * 3. 发送请求：POST /api/v1/production/orders/123/complete
     * 4. 后端处理：ProductionOrderController@complete 方法
     *    - 验证订单状态（必须是"进行中"状态）
     *    - 更新订单状态为"已完成"
     *    - 记录实际结束时间（actualEndDate）
     *    - 记录完成人
     *    - 返回更新后的订单
     * 5. 前端处理：
     *    - 显示成功提示
     *    - 更新页面显示的状态和结束时间
     * 
     * 代码示例：
     * ```typescript
     * // 完成生产订单
     * const completed = await productionApi.completeProductionOrder(123)
     * console.log(completed.status)  // ProductionOrderStatus.COMPLETED
     * console.log(completed.actualEndDate)  // 实际结束时间
     * 
     * // 在详情页中使用
     * const handleComplete = async () => {
     *   try {
     *     const result = await productionApi.completeProductionOrder(orderId.value)
     *     message.success('生产订单已完成')
     *     orderData.value = result
     *   } catch (error) {
     *     message.error('完成失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：生产订单不存在
     * - 400：订单状态不允许完成（如未开始、已取消等）
     * - 403：无权限操作
     */
    completeProductionOrder(id: number) {
      return client.post<ProductionOrder>(API_ROUTES.production.complete(id))
    },

    /**
     * 取消生产订单
     * 
     * 使用场景：
     * - 取消生产订单
     * - 将订单状态变为"已取消"
     * - 记录取消原因
     * 
     * @param id - 生产订单的 ID
     * @param data - 取消数据
     *   - reason: string - 取消原因（必填）
     * 
     * @returns Promise<ProductionOrder> 更新后的生产订单对象
     * 
     * 数据流向：
     * 1. 用户在生产订单详情页点击"取消"按钮，输入取消原因
     * 2. 前端调用：productionApi.cancelProductionOrder(123, { reason: '客户取消订单' })
     * 3. 发送请求：POST /api/v1/production/orders/123/cancel
     *    请求体：{ reason: '客户取消订单' }
     * 4. 后端处理：ProductionOrderController@cancel 方法
     *    - 验证订单状态（必须是可取消的状态）
     *    - 更新订单状态为"已取消"
     *    - 记录取消原因和取消人
     *    - 记录取消时间
     *    - 返回更新后的订单
     * 5. 前端处理：
     *    - 显示成功提示
     *    - 更新页面显示的状态
     * 
     * 代码示例：
     * ```typescript
     * // 取消生产订单
     * const cancelled = await productionApi.cancelProductionOrder(123, {
     *   reason: '客户取消订单'
     * })
     * console.log(cancelled.status)  // ProductionOrderStatus.CANCELLED
     * 
     * // 在详情页中使用
     * const handleCancel = async () => {
     *   if (!cancelReason.value) {
     *     message.warning('请输入取消原因')
     *     return
     *   }
     *   
     *   try {
     *     const result = await productionApi.cancelProductionOrder(orderId.value, {
     *       reason: cancelReason.value
     *     })
     *     message.success('订单已取消')
     *     orderData.value = result
     *   } catch (error) {
     *     message.error('取消失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：生产订单不存在
     * - 400：订单状态不允许取消（如已完成、已取消等）
     * - 422：取消原因未提供或格式错误
     * - 403：无权限操作
     */
    cancelProductionOrder(id: number, data: CancelProductionOrderForm) {
      return client.post<ProductionOrder>(API_ROUTES.production.cancel(id), data)
    },

    /**
     * 暂停生产订单
     *
     * 暂停执行中的生产订单，同时级联暂停所有进行中的关联工单
     *
     * @param id - 生产订单ID
     * @param data - 暂停原因（可选）
     */
    pauseProductionOrder(id: number, data?: { reason?: string }) {
      return client.post<ProductionOrder>(API_ROUTES.production.ordersPause(id), data)
    },

    /**
     * 恢复生产（从暂停 → 进行中）
     *
     * 与 restoreProductionOrder 不同：
     * - resume: 暂停状态 → 进行中（不涉及工单状态变更）
     * - restore: 取消状态 → 已审核（可选恢复关联工单）
     *
     * @param id - 生产订单ID
     * @param data - 恢复原因（可选）
     */
    resumeProductionOrder(id: number, data?: { reason?: string }) {
      return client.post<ProductionOrder>(API_ROUTES.production.ordersResume(id), data)
    },

    /**
     * 恢复已取消的生产订单
     *
     * 恢复后订单状态变为「已审核」，可选择是否恢复关联的已取消工单
     *
     * @param id - 生产订单ID
     * @param data - 恢复参数
     */
    restoreProductionOrder(id: number, data?: {
      reason?: string
      restore_work_orders?: boolean
      work_order_ids?: number[]
    }) {
      return client.post<ProductionOrder>(API_ROUTES.production.ordersRestore(id), data)
    },

    /**
     * 获取生产订单状态统计
     * 
     * 使用场景：
     * - 在生产订单管理页面的统计卡片中显示
     * - 查看各状态订单的数量分布
     * - 用于数据分析和报表
     * 
     * @param params - 查询参数（可选，用于筛选统计范围）
     *   - 支持与 getProductionOrderList 相同的筛选参数
     *   - 如：material_code, plan_start_from, plan_start_to 等
     * 
     * @returns Promise<ProductionOrderStatusStatistics> 状态统计对象
     *   返回的数据结构：
     *   {
     *     pending: number,      // 待接单数量
     *     accepted: number,     // 已接单数量
     *     inProgress: number,   // 进行中数量
     *     completed: number,    // 已完成数量
     *     paused: number,       // 已暂停数量
     *     cancelled: number,    // 已取消数量
     *   }
     * 
     * 数据流向：
     * 1. 用户打开生产订单管理页面
     * 2. 前端调用：productionApi.getProductionOrderStatistics()
     * 3. 发送请求：GET /api/v1/production/orders/statistics
     * 4. 后端处理：ProductionOrderController@statistics 方法
     *    - 查询数据库，按状态分组统计
     *    - 应用筛选条件（如果有）
     *    - 返回统计结果
     * 5. 前端处理：
     *    - 在统计卡片中显示各状态的数量
     * 
     * 代码示例：
     * ```typescript
     * // 获取全部统计
     * const stats = await productionApi.getProductionOrderStatistics()
     * console.log(stats.pending)  // 待接单数量
     * console.log(stats.completed)  // 已完成数量
     * 
     * // 获取指定物料的统计
     * const materialStats = await productionApi.getProductionOrderStatistics({
     *   material_code: 'M001'
     * })
     * 
     * // 在页面中使用
     * const loadStatistics = async () => {
     *   const stats = await productionApi.getProductionOrderStatistics()
     *   statisticsData.value = stats
     * }
     * ```
     * 
     * 错误处理：
     * - 500：服务器内部错误（数据库查询失败等）
     */
    getProductionOrderStatistics(params?: ProductionOrderQueryParams) {
      return client.get<ProductionOrderStatusStatistics>(
        API_ROUTES.production.statistics,
        { params }
      )
    },

    // ------------------------------------------------------------------------
    // 1.1 工单分配（Phase 2 MPS）
    // ------------------------------------------------------------------------

    /**
     * 获取工单分配预览
     * 
     * 使用场景：
     * - 用户在生产订单详情页点击"分配工单"按钮
     * - 系统自动计算分配方案并返回预览结果
     * - 用户可以查看建议的工单列表、产能负荷、冲突警告
     * 
     * @param id - 生产订单ID
     * @param data - 分配预览请求参数
     *   - strategy: 分配策略（balanced/fastest/least_change）
     * 
     * @returns Promise<DispatchPreviewResult> 分配预览结果
     *   包含：productionOrder, suggestedWorkOrders, conflicts, capacityUtilization
     * 
     * 数据流向：
     * 1. 用户点击"分配工单"按钮
     * 2. 前端调用：productionApi.getDispatchPreview(123, { strategy: 'balanced' })
     * 3. 发送请求：POST /api/v1/production/orders/123/dispatch/preview
     * 4. 后端处理：ProductionOrderController@dispatchPreview
     *    - 根据订单信息和产能计算分配方案
     *    - 返回建议的工单列表和产能负荷
     * 5. 前端处理：展示分配预览页面
     * 
     * 代码示例：
     * ```typescript
     * const preview = await productionApi.getDispatchPreview(orderId, {
     *   strategy: 'balanced'
     * })
     * 
     * // 显示建议工单
     * suggestedWorkOrders.value = preview.suggestedWorkOrders
     * 
     * // 显示产能负荷
     * capacityUtilization.value = preview.capacityUtilization
     * 
     * // 显示冲突警告
     * if (preview.conflicts.length > 0) {
     *   message.warning(`发现 ${preview.conflicts.length} 个潜在问题`)
     * }
     * ```
     * 
     * 错误处理：
     * - 404：生产订单不存在
     * - 400：订单状态不允许分配（如已完成、已取消）
     */
    getDispatchPreview(id: number, data?: DispatchPreviewRequest) {
      return client.post<DispatchPreviewResult>(
        API_ROUTES.production.preview(id),
        data || { strategy: 'balanced' }
      )
    },

    /**
     * 确认工单分配
     * 
     * 使用场景：
     * - 用户在分配预览页面确认分配方案
     * - 批量创建工单并更新订单状态
     * 
     * @param id - 生产订单ID
     * @param data - 确认分配请求参数
     *   - workOrders: 要创建的工单列表
     * 
     * @returns Promise<DispatchConfirmResult> 确认分配结果
     *   包含：productionOrder, workOrders
     * 
     * 数据流向：
     * 1. 用户在分配预览页面调整工单（可选）
     * 2. 用户点击"确认分配"按钮
     * 3. 前端调用：productionApi.confirmDispatch(123, { workOrders: [...] })
     * 4. 发送请求：POST /api/v1/production/orders/123/dispatch/confirm
     * 5. 后端处理：ProductionOrderController@dispatchConfirm
     *    - 批量创建工单
     *    - 更新订单状态
     * 6. 前端处理：
     *    - 显示成功消息
     *    - 跳转到订单详情页
     * 
     * 代码示例：
     * ```typescript
     * const result = await productionApi.confirmDispatch(orderId, {
     *   workOrders: selectedWorkOrders.map(wo => ({
     *     batchNo: wo.batchNo,
     *     materialId: wo.materialId,
     *     lineId: wo.lineId,
     *     routeId: wo.routeId,
     *     workOrderQuantity: wo.workOrderQuantity,
     *     planStartDate: wo.planStartDate,
     *     planEndDate: wo.planEndDate,
     *   }))
     * })
     * 
     * message.success(`成功创建 ${result.workOrders.length} 个工单`)
     * router.push(`/planning/${orderId}`)
     * ```
     * 
     * 错误处理：
     * - 404：生产订单不存在
     * - 400：订单状态不允许分配
     * - 422：工单数据验证失败
     */
    confirmDispatch(id: number, data: DispatchConfirmRequest) {
      return client.post<DispatchConfirmResult>(
        API_ROUTES.production.confirm(id),
        data
      )
    },

    /**
     * 获取订单变更历史（Phase 4）
     * 
     * 使用场景：
     * - 在订单详情页面显示变更历史
     * - 追溯订单的修改记录
     * 
     * @param id - 生产订单ID
     * 
     * @returns Promise<ProductionOrderChangelog[]> 变更记录列表
     * 
     * 数据流向：
     * 1. 前端调用：productionApi.getOrderChangelog(123)
     * 2. 发送请求：GET /api/v1/production/orders/123/changelog
     * 3. 后端处理：ProductionOrderController@changelog
     * 4. 前端处理：显示变更历史时间线
     * 
     * 代码示例：
     * ```typescript
     * const changelogs = await productionApi.getOrderChangelog(orderId)
     * changelogs.forEach(log => {
     *   console.log(`${log.fieldLabel}: ${log.oldValue} → ${log.newValue}`)
     * })
     * ```
     */
    getOrderChangelog(id: number) {
      return client.get<ProductionOrderChangelog[]>(
        API_ROUTES.production.ordersChangelog(id)
      )
    },

    /**
     * 获取计划工序进度汇总
     * 
     * 使用场景：
     * - 以计划维度查看所有工序（如13个工序）的进度
     * - 每个工序的计划数量、完成数量、待生产数量
     * - 下料工序的可用子物料库存（FIFO）
     * - 子工单（半成品工单）的状态和可领用批次
     * 
     * @param id - 生产订单ID
     * 
     * @returns Promise<PlanOperationProgressResponse> 工序进度汇总
     * 
     * 数据流向：
     * 1. 前端调用：productionApi.getOrderOperationProgress(123)
     * 2. 发送请求：GET /api/v1/production/orders/123/operation-progress
     * 3. 后端处理：ProductionOrderController@operationProgress
     *    - 聚合该计划下所有工单的工序数据
     *    - 查询BOM物料的可用库存（FIFO）
     *    - 返回工序进度汇总
     * 4. 前端处理：
     *    - 在计划详情页或看板中显示工序进度
     *    - 展示每个工序的完成率和待生产数量
     * 
     * 代码示例：
     * ```typescript
     * const progress = await productionApi.getOrderOperationProgress(orderId)
     * 
     * // 遍历工序进度
     * progress.operations.forEach(op => {
     *   console.log(`${op.operationName}: ${op.completionRate}% (待产: ${op.totalPending})`)
     * })
     * 
     * // 查看首道工序的可用物料
     * const firstOp = progress.operations[0]
     * firstOp?.availableMaterials.forEach(mat => {
     *   console.log(`${mat.materialName}: 需求${mat.requiredQuantity}, 可用${mat.availableQuantity}`)
     * })
     * 
     * // 查看子工单（下料工单）进度
     * progress.childWorkOrders.forEach(child => {
     *   console.log(`${child.workOrderNo}: ${child.completionRate}%, 产出状态: ${child.outputStatus}`)
     * })
     * ```
     * 
     * 错误处理：
     * - 404：生产订单不存在
     */
    getOrderOperationProgress(id: number) {
      return client.get<PlanOperationProgressResponse>(
        API_ROUTES.production.ordersOperationProgress(id)
      )
    },

    /**
     * 获取产线产能
     * 
     * 使用场景：
     * - 在分配预览页面显示产能负荷
     * - 在甘特图页面显示产能信息
     * 
     * @param params - 查询参数
     *   - lineIds: 生产线ID数组（空则查询全部）
     *   - startDate: 开始日期
     *   - endDate: 结束日期
     * 
     * @returns Promise<LineCapacitySummary[]> 产线产能列表
     * 
     * 数据流向：
     * 1. 前端调用：productionApi.getLineCapacity({ startDate, endDate })
     * 2. 发送请求：GET /api/v1/production/capacity/lines
     * 3. 后端处理：ProductionOrderController@capacityLines
     *    - 查询各产线的产能配置
     *    - 计算每日负荷
     * 4. 前端处理：显示产能负荷图
     * 
     * 代码示例：
     * ```typescript
     * const capacities = await productionApi.getLineCapacity({
     *   startDate: '2025-12-01',
     *   endDate: '2025-12-07',
     * })
     * 
     * capacities.forEach(line => {
     *   console.log(`${line.lineName}:`)
     *   line.dailyLoads.forEach(load => {
     *     console.log(`  ${load.date}: ${load.utilizationRate * 100}%`)
     *   })
     * })
     * ```
     */
    getLineCapacity(params: LineCapacityQueryParams) {
      return client.get<LineCapacitySummary[]>(
        API_ROUTES.production.capacityLines,
        { params }
      )
    },

    // ========================================================================
    // 2. 工单（WorkOrder）相关 API
    // ========================================================================
    // 📌 API 前缀：/api/v1/production/work-orders
    // 📌 功能：工单的创建、派发、接单、执行、完成等全生命周期管理
    //         包含工序、进度、设备绑定、批次标签等扩展功能

    /**
     * 获取工单列表（分页）
     * 
     * 使用场景：
     * - 在工单管理页面的主列表中使用
     * - 支持分页、搜索、筛选功能
     * - 用户可以根据各种条件查询工单
     * 
     * @param params - 查询参数对象（可选）
     *   - page?: number - 页码，从 1 开始，默认 1
     *   - per_page?: number - 每页数量，默认 20，最大 100
     *   - work_order_no?: string - 工单编号（精确匹配）
     *   - production_order_id?: number - 生产订单ID
     *   - status?: WorkOrderStatus | WorkOrderStatus[] - 工单状态（单个或数组）
     *   - priority?: WorkOrderPriority - 优先级
     *   - workstation_id?: number - 工位ID
     *   - operator_id?: number - 操作工ID
     *   - keyword?: string - 搜索关键词
     *   - sort_by?: string - 排序字段
     *   - sort_order?: 'asc' | 'desc' - 排序方向，默认 'desc'
     * 
     * @returns Promise<PaginationResult<WorkOrder>> 分页结果对象
     * 
     * 代码示例：
     * ```typescript
     * // 获取工单列表
     * const workOrders = await productionApi.getWorkOrderList({ page: 1 })
     * 
     * // 按状态筛选
     * const pendingOrders = await productionApi.getWorkOrderList({
     *   status: WorkOrderStatus.PENDING
     * })
     * ```
     */
    getWorkOrderList(params?: WorkOrderQueryParams) {
      return client.get<PaginationResult<WorkOrder>>(
        API_ROUTES.production.workOrders,
        { params }
      )
    },

    /**
     * 获取工单详情
     * 
     * 使用场景：
     * - 在工单详情页面显示完整信息
     * - 编辑工单时获取当前数据
     * 
     * @param id - 工单的唯一标识符（主键）
     * @returns Promise<WorkOrder> 单个工单对象
     */
    getWorkOrderDetail(id: number) {
      return client.get<WorkOrder>(API_ROUTES.production.productionWorkOrders(id))
    },

    /**
     * 根据工单号或批次号获取工单详情
     *
     * 使用场景：
     * - 扫码工单标签后，按工单号获取最新工单信息
     * - 扫码批次标签后，反查所属工单
     * - 追溯入口根据工单号解析批次号
     *
     * @param code - 工单号或批次号
     * @returns Promise<WorkOrder> 匹配到的工单对象
     */
    getWorkOrderByCode(code: string) {
      return client.get<WorkOrder>(API_ROUTES.production.workOrdersByCode, {
        params: { code },
      })
    },

    /**
     * 创建工单
     * 
     * 使用场景：
     * - 在工单管理页面点击"新建"按钮
     * - 提交创建工单的表单
     * 
     * @param data - 创建工单的表单数据
     * @returns Promise<WorkOrder> 创建成功后的工单对象
     */
    createWorkOrder(data: CreateWorkOrderForm) {
      return client.post<WorkOrder>(API_ROUTES.production.workOrders, data)
    },

    /**
     * 更新工单
     * 
     * 使用场景：
     * - 在工单编辑页面修改后提交
     * 
     * @param id - 要更新的工单的 ID
     * @param data - 更新的数据
     * @returns Promise<WorkOrder> 更新后的完整工单对象
     */
    updateWorkOrder(id: number, data: UpdateWorkOrderForm) {
      return client.put<WorkOrder>(API_ROUTES.production.productionWorkOrders(id), data)
    },

    /**
     * 删除工单
     * 
     * 使用场景：
     * - 在工单列表中点击"删除"按钮
     * 
     * @param id - 要删除的工单的 ID
     * @returns Promise<void> 删除成功不返回数据
     */
    deleteWorkOrder(id: number) {
      return client.delete<void>(API_ROUTES.production.productionWorkOrders(id))
    },

    /**
     * 接单（接受工单）
     * 
     * 使用场景：
     * - 操作工接受工单
     * - 将工单状态从"待接单"变为"已接单"
     * 
     * @param id - 工单的 ID
     * @param data - 接单数据（可选）
     * @returns Promise<WorkOrder> 更新后的工单对象
     */
    acceptWorkOrder(id: number, data?: AcceptWorkOrderForm) {
      return client.post<WorkOrder>(API_ROUTES.production.workOrdersAccept(id), data)
    },

    /**
     * 开始执行工单
     * 
     * 使用场景：
     * - 开始执行工单
     * - 将工单状态从"已接单"变为"进行中"
     * 
     * @param id - 工单的 ID
     * @param data - 开始数据（可选）
     * @returns Promise<WorkOrder> 更新后的工单对象
     */
    startWorkOrder(id: number, data?: StartWorkOrderForm) {
      return client.post<WorkOrder>(API_ROUTES.production.workOrdersStart(id), data)
    },

    /**
     * 暂停工单
     * 
     * 使用场景：
     * - 暂停正在执行的工单
     * - 将工单状态变为"已暂停"
     * 
     * @param id - 工单的 ID
     * @param data - 暂停数据（可选）
     * @returns Promise<WorkOrder> 更新后的工单对象
     */
    pauseWorkOrder(id: number, data?: PauseWorkOrderForm) {
      return client.post<WorkOrder>(API_ROUTES.production.pause(id), data)
    },

    /**
     * 完成工单
     * 
     * 使用场景：
     * - 完成工单的执行
     * - 将工单状态从"进行中"变为"已完成"
     * 
     * @param id - 工单的 ID
     * @param data - 完成数据（可选）
     * @returns Promise<WorkOrder> 更新后的工单对象
     */
    completeWorkOrder(id: number, data?: CompleteWorkOrderForm) {
      return client.post<WorkOrder>(API_ROUTES.production.workOrdersComplete(id), data)
    },

    /**
     * 取消工单
     * 
     * 使用场景：
     * - 取消工单
     * - 将工单状态变为"已取消"
     * 
     * @param id - 工单的 ID
     * @param data - 取消数据
     * @returns Promise<WorkOrder> 更新后的工单对象
     */
    cancelWorkOrder(id: number, data: CancelWorkOrderForm) {
      return client.post<WorkOrder>(API_ROUTES.production.workOrdersCancel(id), data)
    },

    /**
     * 恢复已取消的工单
     *
     * 恢复前检查关联计划状态，计划已取消则不允许单独恢复工单
     *
     * @param id - 工单ID
     * @param data - 恢复原因（可选）
     */
    restoreWorkOrder(id: number, data?: { reason?: string }) {
      return client.post<WorkOrder>(API_ROUTES.production.workOrdersRestore(id), data)
    },

    /**
     * 获取工单状态统计
     * 
     * 使用场景：
     * - 在工单管理页面的统计卡片中显示
     * 
     * @param params - 查询参数（可选）
     * @returns Promise<WorkOrderStatusStatistics> 状态统计对象
     */
    getWorkOrderStatistics(params?: WorkOrderQueryParams) {
      return client.get<WorkOrderStatusStatistics>(
        API_ROUTES.production.workOrdersStatistics,
        { params }
      )
    },

    /**
     * 获取可接单工单列表
     * 
     * 使用场景：
     * - 操作工查看可接单的工单
     * - 显示待接单且符合接单条件的工单
     * 
     * @param params - 查询参数（可选）
     * @returns Promise<PaginationResult<WorkOrder>> 分页结果对象
     */
    getAvailableWorkOrders(params?: WorkOrderQueryParams) {
      return client.get<PaginationResult<WorkOrder>>(
        API_ROUTES.production.workOrdersAvailable,
        { params }
      )
    },

    /**
     * 获取我已接单的工单列表
     * 
     * 使用场景：
     * - 操作工查看自己已接单的工单
     * - 显示当前用户已接单的工单
     * 
     * @param params - 查询参数（可选）
     * @returns Promise<PaginationResult<WorkOrder>> 分页结果对象
     */
    getAcceptedWorkOrders(params?: WorkOrderQueryParams) {
      return client.get<PaginationResult<WorkOrder>>(
        API_ROUTES.production.workOrdersAccepted,
        { params }
      )
    },

    /**
     * 获取工单工序列表
     * 
     * 使用场景：
     * - 查看工单的所有工序
     * - 显示工序的执行状态
     * 
     * @param id - 工单的 ID
     * @returns Promise<WorkOrderOperation[]> 工序列表
     */
    getWorkOrderOperations(id: number) {
      return client.get<WorkOrderOperation[]>(
        API_ROUTES.production.workOrdersOperations(id)
      )
    },

    /**
     * 获取工单进度信息
     * 
     * 使用场景：
     * - 查看工单的执行进度
     * - 显示完成百分比等信息
     * 
     * @param id - 工单的 ID
     * @returns Promise<any> 进度信息对象
     */
    getWorkOrderProgress(id: number) {
      return client.get<any>(API_ROUTES.production.workOrdersProgress(id))
    },

    /**
     * 获取工单消耗汇总
     * 
     * 使用场景：
     * - 查看工单的物料消耗情况
     * - 显示已投料和计划投料的对比
     * 
     * @param id - 工单的 ID
     * @returns Promise<any> 消耗汇总对象
     */
    getWorkOrderConsumptions(id: number) {
      return client.get<any>(API_ROUTES.production.workOrdersConsumptions(id))
    },

    /**
     * 获取工序物料清单
     * 
     * 使用场景：
     * - 投料扫描防错功能 - 显示工序需要的 BOM 物料清单
     * - 展示需求数量和已投料数量
     * - App 端报工页面使用
     * 
     * @param workOrderId - 工单 ID
     * @param operationId - 工序 ID
     * @returns Promise<{ requiredMaterials: OperationMaterial[] }> 工序物料清单
     * 
     * @example
     * const { requiredMaterials } = await productionApi.getOperationMaterials(123, 456)
     * // requiredMaterials: [{ bomItemId, materialCode, materialName, requiredQuantity, consumedQuantity, unit }]
     */
    getOperationMaterials(workOrderId: number, operationId: number) {
      return client.get<{ requiredMaterials: OperationMaterial[] }>(
        API_ROUTES.production.workOrdersOperationMaterials(workOrderId),
        { params: { operation_id: operationId } }
      )
    },

    /**
     * 绑定设备
     * 
     * 使用场景：
     * - 将设备绑定到工单
     * - 记录工单使用的设备
     * 
     * @param id - 工单的 ID
     * @param data - 绑定数据
     * @param data.equipment_id - 设备ID（必填）
     * @param data.equipment_code - 设备编码（可选）
     * @returns Promise<WorkOrder> 更新后的工单对象
     */
    bindWorkOrderEquipment(id: number, data: {
      equipment_id: number
      equipment_code?: string
    }) {
      return client.post<WorkOrder>(API_ROUTES.production.bindEquipment(id), data)
    },

    /**
     * 打印批次标签
     * 
     * 使用场景：
     * - 打印工单的批次标签
     * - 生成批次追溯标签
     * 
     * @param id - 工单的 ID
     * @param data - 打印数据（可选）
     * @returns Promise<any> 打印结果
     */
    printWorkOrderBatchLabel(id: number, data?: any) {
      return client.post<any>(API_ROUTES.production.printBatchLabel(id), data)
    },

    /**
     * 获取工单批次标签历史
     * 
     * 使用场景：
     * - 查看工单的批次标签打印历史
     * - 显示所有已打印的标签
     * 
     * @param id - 工单的 ID
     * @returns Promise<any[]> 批次标签历史列表
     */
    getWorkOrderBatchLabels(id: number) {
      return client.get<any[]>(API_ROUTES.production.workOrdersBatchLabels(id))
    },

    // ========================================================================
    // 2.5 多级BOM工单 相关 API（2025-12-15 新增）
    // ========================================================================
    // 📌 功能：多级工单生成、工单树查询、半成品入库、子工单状态

    /**
     * 生成多级工单
     * 
     * 使用场景：
     * - 基于生产订单的BOM自动生成多级工单
     * - 创建成品工单（FINAL）和半成品工单（SEMI）
     * - 建立父子工单关联关系
     * 
     * @param data - 生成参数
     * @returns Promise<GenerateMultiLevelWorkOrderResponse> 生成结果
     */
    generateMultiLevelWorkOrder(data: GenerateMultiLevelWorkOrderForm) {
      return client.post<GenerateMultiLevelWorkOrderResponse>(
        API_ROUTES.production.workOrdersGenerateMultiLevel,
        data
      )
    },

    /**
     * 获取工单树形结构
     * 
     * 使用场景：
     * - 查看工单的层级关系
     * - 展示成品工单和其子半成品工单的树形结构
     * 
     * @param id - 工单的 ID
     * @returns Promise<WorkOrderTreeNode> 工单树形结构
     */
    getWorkOrderTree(id: number) {
      return client.get<WorkOrderTreeNode>(API_ROUTES.production.workOrdersTree(id))
    },

    /**
     * 获取子工单状态
     * 
     * 使用场景：
     * - 检查成品工单的子工单完成情况
     * - 判断成品工单是否可以开工
     * 
     * @param id - 父工单的 ID
     * @returns Promise<ChildWorkOrderStatusResponse> 子工单状态信息
     */
    getChildWorkOrderStatus(id: number) {
      return client.get<ChildWorkOrderStatusResponse>(
        API_ROUTES.production.workOrdersChildStatus(id)
      )
    },

    /**
     * 半成品工单产出入库
     * 
     * 使用场景：
     * - 半成品工单完成后，将产出物料入库
     * - 创建库存批次和库存事务
     * 
     * @param id - 半成品工单的 ID
     * @param data - 入库参数
     * @returns Promise<OutputInboundResponse> 入库结果
     */
    outputInbound(id: number, data: OutputInboundForm) {
      return client.post<OutputInboundResponse>(
        API_ROUTES.production.workOrdersOutputInbound(id),
        data
      )
    },

    /**
     * 获取成品工单可领用的半成品批次
     * 
     * 使用场景：
     * - 查看成品工单可以领用哪些半成品
     * - 在成品工单详情页展示可领料清单
     * 
     * @param id - 成品工单的 ID
     * @returns Promise<AvailableSemiBatch[]> 可领用批次列表
     */
    getAvailableSemiBatches(id: number) {
      return client.get<AvailableSemiBatch[]>(
        API_ROUTES.production.workOrdersAvailableSemi(id)
      )
    },

    /**
     * 成品工单自动领用半成品
     * 
     * 使用场景：
     * - 成品工单开始执行时自动领用半成品
     * - 从子工单产出的库存中扣减物料
     * - 创建物料消耗记录和库存事务
     * 
     * @param id - 成品工单的 ID
     * @returns Promise<ConsumeSemiResponse> 领料结果
     */
    consumeSemiFinished(id: number) {
      return client.post<ConsumeSemiResponse>(
        API_ROUTES.production.workOrdersConsumeSemi(id)
      )
    },

    // ========================================================================
    // 3. 报工（WorkReport）相关 API
    // ========================================================================
    // 📌 API 前缀：/api/v1/production/work-reports
    // 📌 功能：报工提交、撤销、统计等
    //         支持单条报工、批量报工、扫码报工

    /**
     * 获取报工列表（分页）
     * 
     * 使用场景：
     * - 在报工管理页面的主列表中使用
     * 
     * @param params - 查询参数对象（可选）
     * @returns Promise<PaginationResult<WorkReport>> 分页结果对象
     */
    getWorkReportList(params?: WorkReportQueryParams) {
      return client.get<PaginationResult<WorkReport>>(
        API_ROUTES.production.workReports,
        { params }
      )
    },

    /**
     * 获取报工详情
     * 
     * 使用场景：
     * - 在报工详情页面显示完整信息
     * 
     * @param id - 报工的唯一标识符（主键）
     * @returns Promise<WorkReport> 单个报工对象
     */
    getWorkReportDetail(id: number) {
      return client.get<WorkReport>(API_ROUTES.production.productionWorkReports(id))
    },

    /**
     * 提交报工
     * 
     * 使用场景：
     * - 操作工提交报工
     * - 记录完成数量、合格数量、不合格数量等
     * 
     * @param data - 报工数据
     * @returns Promise<WorkReport> 创建成功后的报工对象
     */
    createWorkReport(data: CreateWorkReportForm) {
      return client.post<WorkReport>(API_ROUTES.production.workReports, data)
    },

    /**
     * 批量报工
     * 
     * 使用场景：
     * - 批量提交多条报工记录
     * - 提高报工效率
     * 
     * @param data - 批量报工数据（包含多条报工记录）
     * @returns Promise<WorkReport[]> 创建成功后的报工对象数组
     */
    batchCreateWorkReport(data: { reports: CreateWorkReportForm[] }) {
      return client.post<WorkReport[]>(API_ROUTES.production.batch, data)
    },

    /**
     * 撤销报工
     * 
     * 使用场景：
     * - 撤销已提交的报工
     * - 将报工状态变为已撤销
     * 
     * @param id - 报工的 ID
     * @param data - 撤销数据（可选）
     * @returns Promise<WorkReport> 更新后的报工对象
     */
    cancelWorkReport(id: number, data?: CancelWorkReportForm) {
      return client.post<WorkReport>(API_ROUTES.production.workReportsCancel(id), data)
    },

    /**
     * 管理员强制撤销报工
     * 
     * 使用场景：
     * - 报工数据错误且超过24小时
     * - 需要清理异常数据
     * - 入库被拒绝后需要重新报工
     * 
     * 权限要求：admin、super-admin、production_supervisor、warehouse_supervisor
     * 
     * @param id - 报工的 ID
     * @param data - 强制撤销数据（原因必填，至少10个字符）
     * @returns Promise<WorkReport> 更新后的报工对象
     */
    forceCancelWorkReport(id: number, data: ForceCancelWorkReportForm) {
      return client.post<WorkReport>(API_ROUTES.production.workReportsForceCancel(id), data)
    },

    /**
     * 删除报工
     * 
     * 使用场景：
     * - 删除报工记录
     * 
     * @param id - 要删除的报工的 ID
     * @returns Promise<void> 删除成功不返回数据
     */
    deleteWorkReport(id: number) {
      return client.delete<void>(API_ROUTES.production.productionWorkReports(id))
    },

    /**
     * 获取操作工产量统计
     * 
     * 使用场景：
     * - 统计各操作工的产量
     * - 用于绩效考核
     * 
     * @param params - 查询参数（可选，如日期范围等）
     * @returns Promise<OperatorStatisticsItem[]> 操作工统计列表
     */
    getOperatorStatistics(params?: WorkReportQueryParams) {
      return client.get<OperatorStatisticsItem[]>(
        API_ROUTES.production.statisticsOperator,
        { params }
      )
    },

    /**
     * 获取工位产量统计
     * 
     * 使用场景：
     * - 统计各工位的产量
     * - 用于产能分析
     * 
     * @param params - 查询参数（可选）
     * @returns Promise<WorkstationStatisticsItem[]> 工位统计列表
     */
    getWorkstationStatistics(params?: WorkReportQueryParams) {
      return client.get<WorkstationStatisticsItem[]>(
        API_ROUTES.production.statisticsWorkstation,
        { params }
      )
    },

    /**
     * 获取日产量统计
     * 
     * 使用场景：
     * - 统计每日的产量
     * - 用于生产报表
     * 
     * @param params - 查询参数（可选，如日期范围等）
     * @returns Promise<DailyStatisticsItem[]> 日产量统计列表
     */
    getDailyStatistics(params?: WorkReportQueryParams) {
      return client.get<DailyStatisticsItem[]>(
        API_ROUTES.production.statisticsDaily,
        { params }
      )
    },

    // ========================================================================
    // 4. 物料投料（MaterialConsumption）相关 API
    // ========================================================================
    // 📌 API 前缀：/api/v1/production/material-consumptions
    // 📌 功能：物料投料、退料、BOM 防错校验等
    //         与库存系统联动，投料时自动扣减库存

    /**
     * 获取物料投料列表（分页）
     * 
     * 使用场景：
     * - 在物料投料管理页面的主列表中使用
     * 
     * @param params - 查询参数对象（可选）
     * @returns Promise<PaginationResult<MaterialConsumption>> 分页结果对象
     */
    getMaterialConsumptionList(params?: MaterialConsumptionQueryParams) {
      return client.get<PaginationResult<MaterialConsumption>>(
        API_ROUTES.production.materialConsumptions,
        { params }
      )
    },

    /**
     * 获取物料投料详情
     * 
     * 使用场景：
     * - 在物料投料详情页面显示完整信息
     * 
     * @param id - 物料投料的唯一标识符（主键）
     * @returns Promise<MaterialConsumption> 单个物料投料对象
     */
    getMaterialConsumptionDetail(id: number) {
      return client.get<MaterialConsumption>(
        API_ROUTES.production.productionMaterialConsumptions(id)
      )
    },

    /**
     * 投料
     * 
     * 使用场景：
     * - 操作工进行物料投料
     * - 自动扣减库存
     * 
     * @param data - 投料数据
     * @returns Promise<MaterialConsumption> 创建成功后的投料对象
     */
    createMaterialConsumption(data: CreateMaterialConsumptionForm) {
      return client.post<MaterialConsumption>(
        API_ROUTES.production.materialConsumptions,
        data
      )
    },

    /**
     * 批量投料
     * 
     * 使用场景：
     * - 批量提交多条投料记录
     * 
     * @param data - 批量投料数据（包含多条投料记录）
     * @returns Promise<MaterialConsumption[]> 创建成功后的投料对象数组
     */
    batchCreateMaterialConsumption(data: { consumptions: CreateMaterialConsumptionForm[] }) {
      return client.post<MaterialConsumption[]>(
        API_ROUTES.production.materialConsumptionsBatch,
        data
      )
    },

    /**
     * 删除物料投料
     * 
     * 使用场景：
     * - 删除投料记录
     * 
     * @param id - 要删除的物料投料的 ID
     * @returns Promise<void> 删除成功不返回数据
     */
    deleteMaterialConsumption(id: number) {
      return client.delete<void>(
        API_ROUTES.production.productionMaterialConsumptions(id)
      )
    },

    /**
     * BOM 防错校验
     * 
     * 使用场景：
     * - 投料扫描防错功能 - 校验扫描的物料是否在当前工序 BOM 中
     * - 防止操作员投错物料
     * - 检查投料数量是否超限
     * - App 端报工页面扫码后调用
     * 
     * @param data - 校验请求数据
     * @param data.work_order_id - 工单 ID（必填）
     * @param data.operation_id - 工序 ID（可选，工序级校验时必填）
     * @param data.material_code - 物料编码（必填）
     * @param data.quantity - 数量（可选，用于检查是否超限）
     * @returns Promise<MaterialValidation> 校验结果
     * 
     * @example
     * const result = await productionApi.checkBom({
     *   work_order_id: 123,
     *   operation_id: 456,
     *   material_code: 'MAT-001',
     *   quantity: 10
     * })
     * if (result.isValid) {
     *   // 校验通过，可以投料
     *   console.log(result.materialInfo)
     * } else {
     *   // 校验失败，显示错误信息
     *   console.error(result.message)
     * }
     */
    checkBom(data: MaterialCheckBomRequest) {
      return client.post<MaterialValidationResponse>(API_ROUTES.production.checkBom, data)
    },

    /**
     * 退料
     * 
     * 使用场景：
     * - 将已投料的物料退回库存
     * - 自动增加库存
     * 
     * @param data - 退料数据（包含投料ID、退料数量等）
     * @returns Promise<any> 退料结果
     */
    returnMaterial(data: any) {
      return client.post<any>(API_ROUTES.production.return, data)
    },

    // ========================================================================
    // 5. 报废单（ScrapOrder）相关 API
    // ========================================================================
    // 📌 API 前缀：/api/v1/production/scrap-orders
    // 📌 功能：报废单的创建、审核、查询等
    //         记录生产过程中的报废情况，便于质量追溯

    /**
     * 获取报废单列表（分页）
     * 
     * 使用场景：
     * - 在报废单管理页面的主列表中使用
     * 
     * @param params - 查询参数对象（可选）
     * @returns Promise<PaginationResult<ScrapOrder>> 分页结果对象
     */
    getScrapOrderList(params?: ScrapOrderQueryParams) {
      return client.get<PaginationResult<ScrapOrder>>(
        API_ROUTES.production.scrapOrders,
        { params }
      )
    },

    /**
     * 获取报废单详情
     * 
     * 使用场景：
     * - 在报废单详情页面显示完整信息
     * 
     * @param id - 报废单的唯一标识符（主键）
     * @returns Promise<ScrapOrder> 单个报废单对象
     */
    getScrapOrderDetail(id: number) {
      return client.get<ScrapOrder>(
        API_ROUTES.production.productionScrapOrders(id)
      )
    },

    /**
     * 创建报废单
     * 
     * 使用场景：
     * - 创建报废单记录
     * 
     * @param data - 报废单数据
     * @returns Promise<ScrapOrder> 创建成功后的报废单对象
     */
    createScrapOrder(data: CreateScrapOrderForm) {
      return client.post<ScrapOrder>(API_ROUTES.production.scrapOrders, data)
    },

    /**
     * 更新报废单
     * 
     * 使用场景：
     * - 在报废单编辑页面修改后提交
     * 
     * @param id - 要更新的报废单的 ID
     * @param data - 更新的数据
     * @returns Promise<ScrapOrder> 更新后的完整报废单对象
     */
    updateScrapOrder(id: number, data: UpdateScrapOrderForm) {
      return client.put<ScrapOrder>(
        API_ROUTES.production.productionScrapOrders(id),
        data
      )
    },

    /**
     * 删除报废单
     * 
     * 使用场景：
     * - 删除报废单记录
     * 
     * @param id - 要删除的报废单的 ID
     * @returns Promise<void> 删除成功不返回数据
     */
    deleteScrapOrder(id: number) {
      return client.delete<void>(
        API_ROUTES.production.productionScrapOrders(id)
      )
    },

    /**
     * 审核报废单
     * 
     * 使用场景：
     * - 审核人员审核报废单
     * - 将报废单状态变为"已审核"或"已拒绝"
     * 
     * @param id - 报废单的 ID
     * @param data - 审核数据（包含审核结果、审核意见等）
     * @returns Promise<ScrapOrder> 更新后的报废单对象
     */
    approveScrapOrder(id: number, data: ApproveScrapOrderForm | RejectScrapOrderForm) {
      return client.post<ScrapOrder>(
        API_ROUTES.production.scrapOrdersApprove(id),
        data
      )
    },

    // ========================================================================
    // 5.1 报废单统计 API
    // ========================================================================

    /**
     * 获取报废单统计概况
     */
    getScrapStatsSummary(params?: ScrapStatisticsQueryParams) {
      return client.get<ScrapStatsSummary>(
        API_ROUTES.production.scrapOrdersStatsSummary,
        { params }
      )
    },

    /**
     * 获取报废单按类型统计
     */
    getScrapStatsByType(params?: ScrapStatisticsQueryParams) {
      return client.get<ScrapStatsByType[]>(
        API_ROUTES.production.scrapOrdersStatsByType,
        { params }
      )
    },

    /**
     * 获取报废单按处置方式统计
     */
    getScrapStatsByDisposal(params?: ScrapStatisticsQueryParams) {
      return client.get<ScrapStatsByDisposal[]>(
        API_ROUTES.production.scrapOrdersStatsByDisposal,
        { params }
      )
    },

    /**
     * 获取报废单按物料统计
     */
    getScrapStatsByMaterial(params?: ScrapStatisticsQueryParams) {
      return client.get<ExceptionStatsByMaterial[]>(
        API_ROUTES.production.scrapOrdersStatsByMaterial,
        { params }
      )
    },

    /**
     * 获取报废单按原因统计
     */
    getScrapStatsByReason(params?: ScrapStatisticsQueryParams) {
      return client.get<ExceptionStatsByReason[]>(
        API_ROUTES.production.scrapOrdersStatsByReason,
        { params }
      )
    },

    /**
     * 获取报废单时间趋势统计
     */
    getScrapStatsTrend(params?: ExceptionStatsTrendQueryParams) {
      return client.get<ExceptionStatsTrend>(
        API_ROUTES.production.scrapOrdersStatsTrend,
        { params }
      )
    },

    // ========================================================================
    // 6. 返工单（ReworkOrder）相关 API
    // ========================================================================
    // 📌 API 前缀：/api/v1/production/rework-orders
    // 📌 功能：返工单的创建、审核、执行、完成等
    //         管理不良品的返工处理流程

    /**
     * 获取返工单列表（分页）
     * 
     * 使用场景：
     * - 在返工单管理页面的主列表中使用
     * 
     * @param params - 查询参数对象（可选）
     * @returns Promise<PaginationResult<ReworkOrder>> 分页结果对象
     */
    getReworkOrderList(params?: ReworkOrderQueryParams) {
      return client.get<PaginationResult<ReworkOrder>>(
        API_ROUTES.production.reworkOrders,
        { params }
      )
    },

    /**
     * 获取返工单详情
     * 
     * 使用场景：
     * - 在返工单详情页面显示完整信息
     * 
     * @param id - 返工单的唯一标识符（主键）
     * @returns Promise<ReworkOrder> 单个返工单对象
     */
    getReworkOrderDetail(id: number) {
      return client.get<ReworkOrder>(
        API_ROUTES.production.productionReworkOrders(id)
      )
    },

    /**
     * 创建返工单
     * 
     * 使用场景：
     * - 创建返工单记录
     * 
     * @param data - 返工单数据
     * @returns Promise<ReworkOrder> 创建成功后的返工单对象
     */
    createReworkOrder(data: CreateReworkOrderForm) {
      return client.post<ReworkOrder>(API_ROUTES.production.reworkOrders, data)
    },

    /**
     * 更新返工单
     * 
     * 使用场景：
     * - 在返工单编辑页面修改后提交
     * 
     * @param id - 要更新的返工单的 ID
     * @param data - 更新的数据
     * @returns Promise<ReworkOrder> 更新后的完整返工单对象
     */
    updateReworkOrder(id: number, data: UpdateReworkOrderForm) {
      return client.put<ReworkOrder>(
        API_ROUTES.production.productionReworkOrders(id),
        data
      )
    },

    /**
     * 删除返工单
     * 
     * 使用场景：
     * - 删除返工单记录
     * 
     * @param id - 要删除的返工单的 ID
     * @returns Promise<void> 删除成功不返回数据
     */
    deleteReworkOrder(id: number) {
      return client.delete<void>(
        API_ROUTES.production.productionReworkOrders(id)
      )
    },

    /**
     * 审核返工单
     * 
     * 使用场景：
     * - 审核人员审核返工单
     * - 将返工单状态变为"已审核"或"已拒绝"
     * 
     * @param id - 返工单的 ID
     * @param data - 审核数据（包含审核结果、审核意见等）
     * @returns Promise<ReworkOrder> 更新后的返工单对象
     */
    approveReworkOrder(id: number, data: ApproveReworkOrderForm | RejectReworkOrderForm) {
      return client.post<ReworkOrder>(
        API_ROUTES.production.reworkOrdersApprove(id),
        data
      )
    },

    /**
     * 开始执行返工
     * 
     * 使用场景：
     * - 开始执行返工单
     * - 将返工单状态变为"处理中"
     * 
     * @param id - 返工单的 ID
     * @returns Promise<ReworkOrder> 更新后的返工单对象
     */
    startReworkOrder(id: number) {
      return client.post<ReworkOrder>(
        API_ROUTES.production.reworkOrdersStart(id)
      )
    },

    /**
     * 完成返工
     * 
     * 使用场景：
     * - 完成返工单的处理
     * - 将返工单状态变为"已完成"
     * 
     * @param id - 返工单的 ID
     * @param data - 完成数据（可选）
     * @returns Promise<ReworkOrder> 更新后的返工单对象
     */
    completeReworkOrder(id: number, data?: CompleteReworkOrderForm) {
      return client.post<ReworkOrder>(
        API_ROUTES.production.reworkOrdersComplete(id),
        data
      )
    },

    // ========================================================================
    // 6.1 返工单统计 API
    // ========================================================================

    /**
     * 获取返工单统计概况
     */
    getReworkStatsSummary(params?: ReworkStatisticsQueryParams) {
      return client.get<ReworkStatsSummary>(
        API_ROUTES.production.reworkOrdersStatsSummary,
        { params }
      )
    },

    /**
     * 获取返工单按类型统计
     */
    getReworkStatsByType(params?: ReworkStatisticsQueryParams) {
      return client.get<ReworkStatsByType[]>(
        API_ROUTES.production.reworkOrdersStatsByType,
        { params }
      )
    },

    /**
     * 获取返工单按物料统计
     */
    getReworkStatsByMaterial(params?: ReworkStatisticsQueryParams) {
      return client.get<ExceptionStatsByMaterial[]>(
        API_ROUTES.production.reworkOrdersStatsByMaterial,
        { params }
      )
    },

    /**
     * 获取返工单按原因统计
     */
    getReworkStatsByReason(params?: ReworkStatisticsQueryParams) {
      return client.get<ExceptionStatsByReason[]>(
        API_ROUTES.production.reworkOrdersStatsByReason,
        { params }
      )
    },

    /**
     * 获取返工单时间趋势统计
     */
    getReworkStatsTrend(params?: ExceptionStatsTrendQueryParams) {
      return client.get<ExceptionStatsTrend>(
        API_ROUTES.production.reworkOrdersStatsTrend,
        { params }
      )
    },

    // ========================================================================
    // 7. 生产批次（ProductionBatch）相关 API
    // ========================================================================
    // 📌 API 前缀：/api/v1/production/batches
    // 📌 功能：批次查询、追溯链查看、标签重打、容器更换等
    //         是生产追溯系统的核心模块

    /**
     * 获取生产批次列表（分页）
     * 
     * 使用场景：
     * - 在生产批次管理页面的主列表中使用
     * 
     * @param params - 查询参数对象（可选）
     * @returns Promise<PaginationResult<ProductionBatch>> 分页结果对象
     */
    getProductionBatchList(params?: ProductionBatchQueryParams) {
      return client.get<PaginationResult<ProductionBatch>>(
        API_ROUTES.production.productionBatches(''),
        { params }
      )
    },

    /**
     * 搜索生产批次（扫码）
     * 
     * 使用场景：
     * - 通过批次编号搜索批次
     * - 扫码查询批次信息
     * 
     * @param params - 查询参数（包含批次编号等）
     * @returns Promise<ProductionBatch> 批次对象
     */
    searchProductionBatch(params: { batch_no?: string; [key: string]: any }) {
      return client.get<ProductionBatch>(
        API_ROUTES.production.batchesSearch,
        { params }
      )
    },

    /**
     * 获取生产批次详情
     * 
     * 使用场景：
     * - 在生产批次详情页面显示完整信息
     * 
     * @param batchNo - 批次编号（不是ID，是批次编号）
     * @returns Promise<ProductionBatch> 单个生产批次对象
     */
    getProductionBatchDetail(batchNo: string) {
      return client.get<ProductionBatch>(
        API_ROUTES.production.productionBatches(batchNo)
      )
    },

    /**
     * 获取批次追溯链（完整追溯数据）
     * 
     * 使用场景：
     * - 查看批次的完整追溯信息
     * - 包含：报工记录、原材料投料、容器更换、汇总统计
     * 
     * @param batchNo - 批次编号
     * @returns Promise<BatchTraceResult> 完整追溯结果
     */
    getProductionBatchWorkReports(batchNo: string) {
      return client.get<BatchTraceResult>(
        API_ROUTES.production.batchesWorkReports(batchNo)
      )
    },

    /**
     * 标签重打
     * 
     * 使用场景：
     * - 重新打印批次标签
     * - 标签损坏或丢失时使用
     * 
     * @param batchNo - 批次编号
     * @param data - 重打数据（可选）
     * @returns Promise<any> 打印结果
     */
    reprintProductionBatchLabel(batchNo: string, data?: any) {
      return client.post<any>(
        API_ROUTES.production.labelReprints(batchNo),
        data
      )
    },

    /**
     * 容器更换
     * 
     * 使用场景：
     * - 更换批次的容器
     * - 记录容器变更历史
     * 
     * @param batchNo - 批次编号
     * @param data - 更换数据（包含新容器信息等）
     * @returns Promise<any> 更换结果
     */
    changeProductionBatchContainer(batchNo: string, data: any) {
      return client.post<any>(
        API_ROUTES.production.containerChanges(batchNo),
        data
      )
    },

    // ========================================================================
    // 8. WIP 在制品总览相关 API
    // ========================================================================
    // 📌 API 前缀：/api/v1/production/wip
    // 📌 功能：跨工单、工序、产线查看现场理论在制数量

    /**
     * 获取 WIP 在制品总览
     *
     * 使用场景：
     * - Web 端在制品总览页
     * - App 端现场 WIP 快速查看
     * - 基于工单工序累计值计算理论在制数量，不写入库存余额
     *
     * @param params - 查询参数（可选）
     * @returns Promise<WipOverview> WIP 汇总、分组与分页明细
     */
    getWipOverview(params?: WipOverviewQueryParams) {
      return client.get<WipOverview>(API_ROUTES.production.wipOverview, { params })
    },

    /**
     * 获取产品工序进度聚合
     *
     * 使用场景：
     * - Web 端产品 x 工序进度矩阵
     * - 产品工序详情视图
     * - 瓶颈/异常优先级视图
     *
     * @param params - 查询参数（可选）
     * @returns Promise<ProductOperationProgressResponse> 产品工序进度聚合响应
     */
    getProductOperationProgress(params?: ProductOperationProgressQueryParams) {
      return client.get<ProductOperationProgressResponse>(
        API_ROUTES.production.productOperationProgress,
        { params }
      )
    },

    /**
     * 获取 WIP 实时在制库存
     *
     * 使用场景：
     * - Web 端库存视角查看当前理论在制余额
     * - App 端现场按物料、工序、产线、工位快速查看在制积压
     * - 基于工单工序累计值实时聚合，不写入库存台账
     *
     * @param params - 查询参数（可选）
     * @returns Promise<WipRealtimeInventory> 实时在制库存汇总与分页明细
     */
    getWipRealtimeInventory(params?: WipRealtimeInventoryQueryParams) {
      return client.get<WipRealtimeInventory>(API_ROUTES.production.wipRealtimeInventory, { params })
    },

    /**
     * 获取 WIP 批次级列表
     *
     * 使用场景：
     * - Web 端按批次追踪当前在制位置
     * - App 端现场查看批次所在工序、容器和在制数量
     * - 基于工单工序累计值实时聚合，不写入库存台账
     *
     * @param params - 查询参数（可选）
     * @returns Promise<WipBatch> 批次级 WIP 汇总与分页明细
     */
    getWipBatches(params?: WipBatchQueryParams) {
      return client.get<WipBatch>(API_ROUTES.production.wipBatches, { params })
    },

    /**
     * 获取 WIP 设备/工位级列表
     *
     * 使用场景：
     * - Web 端按设备和工位查看当前理论在制积压
     * - App 端现场快速定位设备上的工序和在制数量
     * - 基于工单工序累计值实时聚合，不写入库存台账
     *
     * @param params - 查询参数（可选）
     * @returns Promise<WipEquipment> 设备/工位级 WIP 汇总与分页明细
     */
    getWipEquipment(params?: WipEquipmentQueryParams) {
      return client.get<WipEquipment>(API_ROUTES.production.wipEquipment, { params })
    },

    // ========================================================================
    // 9. 生产看板（Dashboard）相关 API
    // ========================================================================
    // 📌 API 前缀：/api/v1/production/dashboard (Web端)
    // 📌 功能：获取生产看板汇总数据

    /**
     * 获取生产看板汇总数据（Web端）
     *
     * 使用场景：
     * - Web 端生产进度看板页面
     * - 实时展示工单进度、产线效率、计划达成率等
     * - 使用标准的用户登录 token 认证
     *
     * @param params - 筛选参数（可选）
     *   - factory_id?: number - 工厂ID
     *   - workshop_id?: number - 车间ID
     *   - line_id?: number - 产线ID
     *
     * @returns Promise<any> 看板数据对象
     *   返回的数据结构：
     *   {
     *     summary: { todayPlan, todayOutput, todayCompleted, achievementRate, qualityRate },
     *     orderStats: { pending, approved, accepted, inProgress, completed, paused, total },
     *     workOrderStats: { pending, accepted, inProgress, completed, paused, total },
     *     lineEfficiency: Array<{ lineId, lineName, plan, output, efficiency }>,
     *     workOrderProgress: Array<{ id, workOrderNo, materialName, progress, ... }>,
     *     hourlyOutput: Array<{ hour, label, output }>,
     *     topProducts: Array<{ materialCode, materialName, displayName, output }>,
     *     keyOperations: Array<{ operationId, operationName, todayOutput, pendingQuantity, progress }>,
     *     timestamp: string
     *   }
     */
    getProductionDashboard(params?: { factory_id?: number; workshop_id?: number; line_id?: number }) {
      return client.get<any>(API_ROUTES.production.dashboard, { params })
    },

    /**
     * 产量统计（四维度：设备 / 工序 / 员工 / 终点统计）
     *
     * 使用场景：
     * - Web 生产看板的产量分析主体
     * - 每行 = 维度值 × 产品的产量明细，支持管理员按维度横向对比
     *
     * 口径：
     * - 基于正式报工事实，排除已撤销报工
     * - terminal 维度只累计到生产统计终点工序（工艺路线模块配置），
     *   未配置终点的路线不计入（不可比），禁止按最大工序号自动推导
     * - equipment 维度为报工口径，与 OEE 自动采集口径不混排
     *
     * @param params - 查询参数
     *   - dimension: 必填，equipment | operation | operator | terminal
     *   - date_from?: string - 报工开始日期 (YYYY-MM-DD)
     *   - date_to?: string - 报工结束日期 (YYYY-MM-DD)
     *   - per_page?: number - 分页大小（1-100，默认 20）
     *
     * @returns Promise<OutputStatisticListResponse> 分页的产量统计行
     */
    getOutputStatistics(params: OutputStatisticQueryParams) {
      return client.get<OutputStatisticListResponse>(
        API_ROUTES.production.dashboardOutputStatistics,
        { params },
      )
    },

    /**
     * 获取生产看板汇总数据（TV端）
     *
     * 使用场景：
     * - TV 大屏生产进度看板
     * - 使用 TV 专用的认证方式（如 TV token 或免认证）
     *
     * @param params - 筛选参数（可选）
     *   - factory_id?: number - 工厂ID
     *   - workshop_id?: number - 车间ID
     *   - line_id?: number - 产线ID
     *
     * @returns Promise<any> 看板数据对象
     */
    getTvProductionDashboard(params?: { factory_id?: number; workshop_id?: number; line_id?: number }) {
      return client.get<any>(API_ROUTES.production.tvDashboard, { params })
    },

    // ========================================================================
    // 8.1 TV 大屏扩展 API（2025-12-31 新增）
    // ========================================================================
    // 📌 API 前缀：/api/v1/tv/production/dashboard
    // 📌 功能：班次产量、报工明细、产量趋势、工序进度、计划进度

    /**
     * 获取班次产量统计
     *
     * 使用场景：
     * - TV 大屏显示各班次产量
     * - 班次配置：早班 8:00-17:30，中班 17:30-02:00
     *
     * @param params - 筛选参数（可选）
     * @returns Promise<ShiftOutput[]> 班次产量列表
     */
    getShiftOutput(params?: { factory_id?: number; workshop_id?: number; line_id?: number }) {
      return client.get<any[]>(API_ROUTES.production.tvDashboardShiftOutput, { params })
    },

    /**
     * 获取最近报工明细
     *
     * 使用场景：
     * - TV 大屏显示最近报工记录
     * - 展示员工、工序、产量、效率等信息
     *
     * @param params - 筛选参数（可选）
     *   - limit?: number - 返回条数，默认20
     * @returns Promise<RecentWorkReport[]> 报工明细列表
     */
    getRecentReports(params?: { factory_id?: number; workshop_id?: number; line_id?: number; limit?: number }) {
      return client.get<any[]>(API_ROUTES.production.tvDashboardRecentReports, { params })
    },

    /**
     * 获取产量趋势（多日对比）
     *
     * 使用场景：
     * - TV 大屏显示近7天产量趋势
     * - 对比实际产量与目标产量
     *
     * @param params - 筛选参数（可选）
     *   - days?: number - 天数，默认7
     * @returns Promise<OutputTrendItem[]> 产量趋势列表
     */
    getOutputTrend(params?: { factory_id?: number; workshop_id?: number; line_id?: number; days?: number }) {
      return client.get<any[]>(API_ROUTES.production.tvDashboardOutputTrend, { params })
    },

    /**
     * 获取工序进度汇总
     *
     * 使用场景：
     * - TV 大屏显示各工序进度
     * - 展示完成、进行中、等待数量
     *
     * @param params - 筛选参数（可选）
     * @returns Promise<DashboardOperationProgress[]> 工序进度列表
     */
    getDashboardOperationProgress(params?: { factory_id?: number; workshop_id?: number; line_id?: number }) {
      return client.get<any[]>(API_ROUTES.production.tvDashboardOperationProgress, { params })
    },

    /**
     * 获取计划进度列表
     *
     * 使用场景：
     * - TV 大屏显示生产计划进度
     * - 展示计划完成情况
     *
     * @param params - 筛选参数（可选）
     *   - limit?: number - 返回条数，默认10
     * @returns Promise<PlanProgressItem[]> 计划进度列表
     */
    getPlanProgress(params?: { factory_id?: number; workshop_id?: number; line_id?: number; limit?: number }) {
      return client.get<any[]>(API_ROUTES.production.tvDashboardPlanProgress, { params })
    },

    // ========================================================================
    // 10. 工单转移（WorkOrder Transfer）相关 API（2026-02-11 新增）
    // ========================================================================
    // 📌 API 前缀：/api/v1/production/work-orders/{id}/transfer-*
    // 📌 功能：获取可转移目标、预览差异、执行转移、查看转移历史

    /**
     * 获取可转移的目标生产订单列表
     *
     * 使用场景：
     * - 工单转移弹窗中，展示可选择的目标生产订单
     * - 按物料一致性排序，相同物料的订单优先展示
     *
     * @param id - 待转移工单ID
     * @returns Promise<TransferableTarget[]> 可转移目标列表
     */
    getTransferTargets(id: number) {
      return client.get<TransferableTarget[]>(API_ROUTES.production.workOrdersTransferTargets(id))
    },

    /**
     * 转移预览 — 检查转移可行性并返回差异信息
     *
     * 使用场景：
     * - 用户选择目标订单后，点击"预览"查看转移影响
     * - 展示可行性校验结果、差异对比、数据快照
     *
     * @param id - 待转移工单ID
     * @param data - 预览请求（包含目标订单ID）
     * @returns Promise<TransferPreviewResult> 预览结果
     */
    getTransferPreview(id: number, data: TransferPreviewRequest) {
      return client.post<TransferPreviewResult>(API_ROUTES.production.workOrdersTransferPreview(id), data)
    },

    /**
     * 执行工单转移
     *
     * 使用场景：
     * - 用户确认预览结果后，点击"确认转移"
     * - 将工单从源生产订单转移到目标生产订单
     * - 自动记录转移日志、更新统计、审计
     *
     * @param id - 待转移工单ID
     * @param data - 转移请求（包含目标订单ID和转移原因）
     * @returns Promise<WorkOrder> 转移后的工单
     */
    transferWorkOrder(id: number, data: TransferRequest) {
      return client.post<WorkOrder>(API_ROUTES.production.workOrdersTransfer(id), data)
    },

    /**
     * 获取工单转移历史
     *
     * 使用场景：
     * - 在工单详情中展示转移记录
     * - 追溯工单的流转路径
     *
     * @param id - 工单ID
     * @returns Promise<TransferHistoryRecord[]> 转移历史列表（按时间倒序）
     */
    getTransferHistory(id: number) {
      return client.get<TransferHistoryRecord[]>(API_ROUTES.production.workOrdersTransferHistory(id))
    },

    /**
     * 获取工单变更历史（含备注修改留痕）
     *
     * 数据来自只追加的审计日志，按时间倒序返回；工单模块不提供删除入口。
     *
     * @param id - 工单ID
     * @returns Promise<WorkOrderChangeHistory> 变更历史列表与总数
     */
    getWorkOrderChangeHistory(id: number) {
      return client.get<WorkOrderChangeHistory>(API_ROUTES.production.workOrdersChangeHistory(id))
    },

    /**
     * 修改工单备注（独立授权码 production:work-order:update-remark）
     *
     * 与通用 updateWorkOrder 解耦：备注不在通用更新请求中放行。
     *
     * @param id - 工单ID
     * @param data - 备注请求（remark 留空表示清空）
     * @returns Promise<WorkOrder> 更新后的工单
     */
    updateWorkOrderRemark(id: number, data: UpdateWorkOrderRemarkForm) {
      return client.put<WorkOrder>(API_ROUTES.production.workOrdersUpdateRemark(id), data)
    },

    // =========================================================================
    // 工序临时变更（v4.0 新增）
    // =========================================================================

    /**
     * 为工单新增工序
     *
     * @param id - 工单ID
     * @param data - 新增参数
     */
    addWorkOrderOperation(id: number, data: {
      operation_id: number
      after_operation_seq?: number | null
      reason?: string
    }) {
      return client.post<any>(API_ROUTES.production.workOrdersAddOperation(id), data)
    },

    /**
     * 删除工单中的未开始工序
     *
     * @param id - 工单ID
     * @param workOrderOperationId - 工单工序表主键ID
     * @param data - 删除原因
     */
    removeWorkOrderOperation(id: number, workOrderOperationId: number, data?: { reason?: string }) {
      return client.delete<any>(API_ROUTES.production.workOrdersRemoveOperation(id, workOrderOperationId), { data })
    },

    /**
     * 跳过工序
     *
     * @param id - 工单ID
     * @param workOrderOperationId - 工单工序表主键ID
     * @param data - 跳过原因
     */
    skipWorkOrderOperation(id: number, workOrderOperationId: number, data?: { reason?: string }) {
      return client.post<any>(API_ROUTES.production.workOrdersSkipOperation(id, workOrderOperationId), data)
    },

    /**
     * 取消跳过工序
     *
     * @param id - 工单ID
     * @param workOrderOperationId - 工单工序表主键ID
     */
    unskipWorkOrderOperation(id: number, workOrderOperationId: number) {
      return client.post<any>(API_ROUTES.production.workOrdersUnskipOperation(id, workOrderOperationId))
    },

    /**
     * 调整工序顺序
     *
     * @param id - 工单ID
     * @param data - 顺序映射 { order_map: { [workOrderOperationId]: new_seq } }
     */
    reorderWorkOrderOperations(id: number, data: { order_map: Record<number, number> }) {
      return client.put<any>(API_ROUTES.production.workOrdersReorderOperations(id), data)
    },

    /**
     * 修改工序参数
     *
     * @param id - 工单ID
     * @param workOrderOperationId - 工单工序表主键ID
     * @param data - 修改参数
     */
    updateWorkOrderOperationParams(id: number, workOrderOperationId: number, data: {
      workstation_id?: number
      standard_time?: number
      plan_quantity?: number
      need_inspection?: boolean
      remark?: string
      reason?: string
    }) {
      return client.put<any>(API_ROUTES.production.workOrdersUpdateOperationParams(id, workOrderOperationId), data)
    },

    /**
     * 获取工序变更历史
     *
     * @param id - 工单ID
     */
    getOperationChanges(id: number) {
      return client.get<OperationChangeRecord[]>(API_ROUTES.production.workOrdersOperationChanges(id))
    },

    /**
     * 计划级批量工序变更
     *
     * @param id - 生产订单ID
     * @param data - 批量变更参数
     */
    batchChangeOperations(id: number, data: {
      action: 'add' | 'remove' | 'skip'
      operation_id: number
      after_operation_seq?: number | null
      work_order_ids?: number[] | null
      reason?: string
    }) {
      return client.post<{ success: number; skipped: number; errors: Array<{ workOrderNo: string; error: string }> }>(
        API_ROUTES.production.ordersBatchChangeOperations(id), data
      )
    },

  }
}
